import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useEmailRules } from './useData';
import { moduleConfig } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { flattenPersonalFolders, useAccessibleAccounts, usePersonalFolders } from '../emails/useData';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { Button, PlusIcon } from '../../components/ui';
import { useNotification } from '../../contexts/NotificationContext';
import { useModuleConfigStrings } from '../../i18n/useModuleConfigStrings';
import api from '../../services/api';
import EmailRuleCard from './components/EmailRuleCard';
import EmailRulesListHeader from './components/EmailRulesListHeader';

const List: React.FC = () => {
  const { colors } = useTheme();
  const { data: rules, isLoading, isError, mutate } = useEmailRules();
  const { data: accounts } = useAccessibleAccounts();
  const { data: personalFolders } = usePersonalFolders();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { showError } = useNotification();
  const strings = useModuleConfigStrings(moduleConfig.module.i18nKey, moduleConfig);
  const navigate = useNavigate();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [userNames, setUserNames] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    void mutate();
  }, [mutate]);

  useEffect(() => {
    api
      .get<
        Array<{
          id: number;
          userDisplayName?: string | null;
          userFirstName?: string;
          userLastName?: string;
          userEmail: string;
        }>
      >('/users')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const map = new Map<number, string>();
        for (const user of list) {
          const label =
            user.userDisplayName?.trim() ||
            [user.userFirstName, user.userLastName].filter(Boolean).join(' ').trim() ||
            user.userEmail;
          map.set(user.id, label);
        }
        setUserNames(map);
      })
      .catch(() => setUserNames(new Map()));
  }, []);

  const accountById = useMemo(() => {
    const map = new Map<number, string>();
    for (const account of accounts) {
      map.set(account.id, account.displayName || account.emailAddress);
    }
    return map;
  }, [accounts]);

  const personalFolderNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const folder of flattenPersonalFolders(personalFolders ?? [])) {
      map.set(folder.id, folder.name);
    }
    return map;
  }, [personalFolders]);

  const summary = useMemo(() => {
    let enabledCount = 0;
    let disabledCount = 0;
    for (const rule of rules) {
      if (rule.enabled) enabledCount += 1;
      else disabledCount += 1;
    }
    return { enabledCount, disabledCount };
  }, [rules]);

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => a.priority - b.priority || a.id - b.id),
    [rules],
  );

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      await api.delete(`/email-rules/${id}`);
      await mutate();
    } catch {
      showError(strings.deleteErrorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading && rules.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] px-4">
        <LoadingSpinner size="large" message="Loading email rules..." />
      </div>
    );
  }

  if (isError && rules.length === 0) {
    return (
      <div className={cn('flex flex-col gap-4 p-4 md:p-6', colors.background)}>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          Failed to load email rules. You may not have permission or the server returned an error.
        </div>
        <Button type="button" variant="outline" onClick={() => void mutate()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 p-4 md:p-6', colors.background)}>
      <EmailRulesListHeader
        totalRules={rules.length}
        enabledCount={summary.enabledCount}
        disabledCount={summary.disabledCount}
        onAdd={() => navigate(moduleConfig.module.route.new)}
      />

      {rules.length === 0 ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-16 text-center',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className={cn(
              'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border',
              colors.border,
              colors.surfaceHover,
              'text-indigo-600 dark:text-indigo-400',
            )}
          >
            <Zap className="h-7 w-7" aria-hidden />
          </div>
          <p className={cn('text-base font-medium', colors.text)}>{strings.emptyNoData}</p>
          <p className={cn('mt-1 max-w-md text-sm', colors.textMuted)}>
            Create rules to organize incoming mail automatically after each sync.
          </p>
          <Button type="button" className="mt-6" onClick={() => navigate(moduleConfig.module.route.new)}>
            <PlusIcon className="h-5 w-5" />
            New rule
          </Button>
        </div>
      ) : (
        <ul className="grid gap-4">
          {sortedRules.map((rule) => {
            const accountLabel =
              rule.emailAccountId != null
                ? accountById.get(rule.emailAccountId) ?? `Account #${rule.emailAccountId}`
                : 'All accounts';

            return (
              <li key={rule.id}>
                <EmailRuleCard
                  rule={rule}
                  accountLabel={accountLabel}
                  detailHref={moduleConfig.module.route.detail(rule.id)}
                  editHref={moduleConfig.module.route.edit(rule.id)}
                  personalFolderNames={personalFolderNames}
                  userNames={userNames}
                  onDelete={() => setDeleteConfirmId(rule.id)}
                  deleting={deletingId === rule.id}
                />
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title={strings.deleteTitle}
        message={strings.deleteMessage}
        confirmText={strings.deleteConfirmText}
        cancelText={strings.deleteCancelText}
        confirmVariant="danger"
      />
    </div>
  );
};

export default List;
